import type { ApplicationListItem } from "./application";

export enum MaterialVideoType {
	Panda = 0,
	YouTube = 1,
}

/** Converte o inteiro da API no enum — único ponto que aceita valor cru do backend. */
export function parseMaterialVideoType(
	raw: number | null | undefined,
): MaterialVideoType | null {
	if (raw === MaterialVideoType.Panda || raw === MaterialVideoType.YouTube) {
		return raw;
	}
	return null;
}

export interface StudyMaterial {
	id: string;
	title: string;
	type: "material";
	thumbnail: string | null;
	material: string; // URL do arquivo
	materialVideo?: string; // URL do vídeo
	materialVideoType?: MaterialVideoType | null;
	stage: number;
	subjects: Array<{ id: string; name: string }>;
	/** ISO ou null quando a API não envia data (ex.: pastas virtuais). */
	updatedAt: string | null;
	isFavorite: boolean;
	sendBy?: string;
}

export interface StudyFolder {
	id: string;
	name: string;
	type: "folder";
	parentFolder: string | null;
	/** ISO ou null quando a API não envia data (ex.: pastas virtuais). */
	updatedAt: string | null;
	subjects?: Array<{ id: string; name: string }>;
}

/**
 * Item de Lista de Exercício exibido dentro de uma pasta de material.
 *
 * É um `ApplicationListItem` (mesmo objeto enxuto consumido em
 * `/painel/listas-de-exercicio`) acrescido de discriminador `type` e
 * atalhos (`title`, `subjects`, `updatedAt`).
 *
 * **Paridade obrigatória:** o `id` aqui é `applicationStudent.id`. Todos os
 * campos de `ApplicationListItem` (finishedIn, canViewGrade,
 * customTimeFinish, application.*, etc.) chegam idênticos ao que a tela
 * de listas-de-exercicio recebe — viabilizado pelo backend
 * `FolderExerciseListSerializer` que herda de
 * `ApplicationListStudentSerializer` no Django.
 *
 * `extends ApplicationListItem` (não `Pick`) garante que campos
 * adicionados ao item enxuto no futuro continuem fluindo sem mudança
 * de tipo.
 */
export interface ExerciseListItem extends ApplicationListItem {
	type: "exercise_list";
	/** Atalho para `application.exam.name`. */
	title: string;
	/** Atalho para `application.exam.subjects` (mesmo shape). */
	subjects: Array<{ id: string; name: string }>;
	/** ISO; vem de `application.updatedAt` no backend. */
	updatedAt: string | null;
}

export interface StudyMaterialsResponse {
	results?: {
		folders: StudyFolder[];
		files: StudyMaterial[];
		exerciseLists?: ExerciseListItem[];
		breadcrumb: Array<{ id: string; name: string }>;
		subjects: Array<{ id: string; name: string }>;
		stages: Array<{ id: number; name: string } | number | string>;
		teachers: Array<{ id: string; name: string }>;
	};
	// Fallback para quando o payload vem direto (não paginado em certas views)
	folders?: StudyFolder[];
	files?: StudyMaterial[];
	exerciseLists?: ExerciseListItem[];
	breadcrumb?: Array<{ id: string; name: string }>;
	subjects?: Array<{ id: string; name: string }>;
	stages?: Array<{ id: number; name: string } | number | string>;
	teachers?: Array<{ id: string; name: string }>;
	count?: number;
	pageSize?: number;
}

import { z } from "zod";

export const StudyMaterialsFilterSchema = z.object({
	title: z.string().optional(),
	q_subjects: z.union([z.string(), z.number()]).optional(),
	q_teacher: z.union([z.string(), z.number()]).optional(),
	q_stage: z.union([z.string(), z.number()]).optional(),
	/* Filtro client-side: derivado dos itens carregados, nunca enviado à API. */
	q_file_type: z.string().optional(),
	preference: z.enum(["recentes", "favoritos"]).optional(),
	view: z.enum(["list", "grid"]).optional(),
	page: z.number().min(1).optional(),
	currentFolderId: z.string().nullable().optional(),
});

export type StudyMaterialsFilterData = z.infer<
	typeof StudyMaterialsFilterSchema
>;

export type StudyMaterialDetail = StudyMaterial;

export interface StudyMaterialsParams extends StudyMaterialsFilterData {
	page_size?: number;
	q_view?:
		| "professores"
		| "disciplinas"
		| "nivel-ensino"
		| "tipo-arquivo"
		| string;
	q_folder_id?: string;
	favorites?: boolean;
	exam?: string | number;
}

export interface FavoriteMaterialPayload {
	materialId: string;
}

export interface FavoriteMaterialResponse {
	success?: boolean;
	status?: string;
	removed?: boolean;
	message: string;
}
